import type { FreeFormObject, RecipeLabwareDef } from '@ctcells/contract'

/**
 * 랩웨어 정의(Opentrons 꼴 `definition`) **읽개** — 치수 · 웰 · 격자.
 *
 * 🔴 **2026-10-11 옮겨 왔다.** 원래 옛 랩웨어 카탈로그 화면(`screens/labware/labwareTables.ts` ·
 *    `labwareModel.ts`)에 있었다. 서버 v0.9.4 가 그 카탈로그(`/labware-definitions`)를 걷어(core `1cd3f92a`)
 *    화면도 걷었는데, 이 셋은 **레시피 안의 랩웨어**(`payload.labware[]` · 같은 꼴)를 읽는 그림·표가 계속 쓴다
 *    (`opentronsLayout.ts` · `RecipeTables.tsx`). 그래서 화면 없이 읽개만 남겼다 — 판단은 그대로다.
 *
 * 🔴 **값을 지어내지 않는다.** 모양이 어긋나면 `null` · 빈 배열이다 — 부르는 쪽이 빈 상태를 낸다.
 */

/** 정의 안의 웰 하나. **읽어낸 것만** 담는다 — 없는 값은 `undefined` 로 남는다 */
export interface WellShape {
  name: string
  x: number
  y: number
  depth?: number
  shape?: string
  diameter?: number
  xDimension?: number
  yDimension?: number
  totalLiquidVolume?: number
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function obj(value: unknown): FreeFormObject | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as FreeFormObject)
    : null
}

/** `definition.dimensions` — 없거나 모양이 어긋나면 `null` */
export function readDimensions(source: FreeFormObject): { x: number; y: number; z: number } | null {
  const raw = obj(source['dimensions'])
  if (raw === null) return null
  const x = num(raw['xDimension'])
  const y = num(raw['yDimension'])
  const z = num(raw['zDimension'])
  return x === undefined || y === undefined || z === undefined ? null : { x, y, z }
}

/**
 * `definition.wells` → 웰 목록.
 *
 * ⚠ **순서는 `ordering`(열 우선)을 따른다.** Opentrons 도 첫 웰(보통 `A1`)을 오프셋
 *   기준으로 쓴다(`labwareInference.ts:53-55`). 객체 키 순서에 기대면 기준 웰이 흔들린다.
 *   `ordering` 이 없으면 `well_names`, 그것도 없으면 키 순서다.
 */
export function readWells(source: FreeFormObject): WellShape[] {
  const wells = obj(source['wells'])
  if (wells === null) return []

  const order = wellOrder(source, Object.keys(wells))
  const shapes: WellShape[] = []
  for (const name of order) {
    const raw = obj(wells[name])
    if (raw === null) continue
    const x = num(raw['x'])
    const y = num(raw['y'])
    // 🔴 좌표가 없는 웰은 **건너뛴다.** 0 으로 메우면 오프셋·간격이 통째로 거짓이 된다
    if (x === undefined || y === undefined) continue
    shapes.push({
      name,
      x,
      y,
      ...(num(raw['depth']) === undefined ? {} : { depth: num(raw['depth']) as number }),
      ...(typeof raw['shape'] === 'string' ? { shape: raw['shape'] } : {}),
      ...(num(raw['diameter']) === undefined ? {} : { diameter: num(raw['diameter']) as number }),
      ...(num(raw['xDimension']) === undefined
        ? {}
        : { xDimension: num(raw['xDimension']) as number }),
      ...(num(raw['yDimension']) === undefined
        ? {}
        : { yDimension: num(raw['yDimension']) as number }),
      ...(num(raw['totalLiquidVolume']) === undefined
        ? {}
        : { totalLiquidVolume: num(raw['totalLiquidVolume']) as number }),
    })
  }
  return shapes
}

function wellOrder(source: FreeFormObject, fallback: string[]): string[] {
  const ordering: unknown = source['ordering']
  if (Array.isArray(ordering)) {
    const flat = ordering.flat()
    if (flat.every((name) => typeof name === 'string') && flat.length > 0) return flat
  }
  const names: unknown = source['well_names']
  if (Array.isArray(names) && names.every((name) => typeof name === 'string') && names.length > 0) {
    return names
  }
  return fallback
}

/**
 * 격자 `[행, 열]` — **정의가 어느 키로 말하든 여기 한 곳에서 읽는다.**
 *
 * | 모드     | 격자를 말하는 키 |
 * | -------- | ---------------- |
 * | `simple` | `grid: { rows, columns }` |
 * | `full`   | `ordering`(열 우선 2차원 배열) — `grid` 가 «없다» |
 *
 * 🔴 **`simple` 의 `grid` 는 `{ rows, columns }` «객체»다**(2026-09-20 실측 —
 *    응답 원문 `"grid":{"rows":2,"columns":3}`). 종전 구현은 `[행, 열]` **배열**을 기다려서
 *    실물 격자를 언제나 `null` 로 흘렸다 — 목이 튜플이라 화면에서만 멀쩡했다.
 *
 * 🔴 **`full` 은 `ordering` 에서 읽는다**(2026-09-21 수정). `recipe.schema.json` 이
 *    `grid` 를 「simple 모드 선택적」으로, `ordering` 을 「full 모드 **열(column)-우선** 정렬
 *    순서」로 못박았다 ⇒ **열 수 = `ordering.length` · 행 수 = `ordering[0].length`**.
 *    종전에는 `full` 이 `null` 로 떨어져 `gridOf` 가 `well_names` 로 `1 × N` 을 «지어냈고»,
 *    실물 `5dae4ea4…`(2×2)가 화면에서 **「1 × 4」** 로 읽혔다 — 틀린 값이다.
 *
 * ⚠ 모양이 어긋나면 `null` 이다. **지어내지 않는다** — 「모른다」가 「1행 N열」보다 정직하다.
 *
 * 🔴 부르는 곳: 레시피 상세의 「랩웨어」 탭(`payload.labware[]` · `RecipeTables.tsx`). 격자 읽기를 두 벌로
 *    두지 마라 — 한쪽만 고쳐져 같은 랩웨어가 두 곳에서 다른 격자로 읽힌다(「1 × 4」 사고가 그 종류였다).
 */
export function readGrid(body: RecipeLabwareDef): [number, number] | null {
  return readSimpleGrid(body) ?? readOrderingGrid(body)
}

function readSimpleGrid(body: RecipeLabwareDef): [number, number] | null {
  const raw: unknown = body.grid
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const { rows, columns } = raw as { rows?: unknown; columns?: unknown }
  return typeof rows === 'number' && typeof columns === 'number' ? [rows, columns] : null
}

/**
 * `ordering` → `[행, 열]`. 🔴 **바깥이 열이다**(스키마 원문 「열(column)-우선」).
 * ⚠ 열마다 길이가 다르면 격자가 아니다 → `null`. 첫 열만 보고 행 수를 단정하지 않는다.
 */
function readOrderingGrid(body: RecipeLabwareDef): [number, number] | null {
  const raw: unknown = body.ordering
  if (!Array.isArray(raw) || raw.length === 0) return null

  const columns = raw as readonly unknown[]
  const shaped = columns.every(
    (column) => Array.isArray(column) && column.every((well) => typeof well === 'string'),
  )
  if (!shaped) return null

  const rows = (columns[0] as readonly string[]).length
  if (rows === 0) return null
  if (!columns.every((column) => (column as readonly string[]).length === rows)) return null

  return [rows, columns.length]
}
